import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebaseApp';
import { isAnalyticsEnabled } from './isAnalyticsEnabled';

const send = (name, params = {}) => {
  if (!isAnalyticsEnabled()) return;
  if (analytics) logEvent(analytics, name, params);
};

export const trackPageView = (path) => {
  send('page_view', { page_path: path, page_title: document.title });
};

export const trackImageUpload = (fileName) => {
  send('image_upload', { file_name: fileName });
};

export const trackShapeSelection = (shape, category) => {
  send('shape_selection', { shape, category });
};

// Single canonical "intent to purchase" event. Fires when the user
// successfully reaches the Zazzle handoff (image uploaded, redirect about
// to open). We never see the actual Zazzle order, so this is the closest
// signal we have.
//
// shaper: { product, kind: 'shaper', shape }
// travel: { product, kind: 'travel', map_type, visited_count, total_count }
export const trackAddToCart = ({ product, kind, shape, mapType, visitedCount, totalCount }) => {
  send('add_to_cart', {
    product,
    kind,
    ...(shape !== undefined && { shape }),
    ...(mapType !== undefined && { map_type: mapType }),
    ...(visitedCount !== undefined && { visited_count: visitedCount }),
    ...(totalCount !== undefined && { total_count: totalCount }),
  });
};

// One canonical download event for both flows.
// shaper:  { kind: 'shaper', shape, category, border_size }
// travel:  { kind: 'travel', map_type, visited_count, total_count }
export const trackDownload = ({ kind, shape, category, borderSize, mapType, visitedCount, totalCount }) => {
  send('download', {
    kind,
    ...(shape !== undefined && { shape }),
    ...(category !== undefined && { category }),
    ...(borderSize !== undefined && { border_size: borderSize }),
    ...(mapType !== undefined && { map_type: mapType }),
    ...(visitedCount !== undefined && { visited_count: visitedCount }),
    ...(totalCount !== undefined && { total_count: totalCount }),
  });
};

export const trackColorChange = (color) => {
  send('color_change', { color });
};

export const trackError = (errorType, errorMessage) => {
  send('error', { error_type: errorType, error_message: errorMessage });
};

export const trackCategorySelection = (categoryName) => {
  send('category_selection', { category: categoryName });
};

export const trackBorderSizeChange = (borderSize) => {
  send('border_size_change', { border_size: borderSize });
};

export const trackColorPickerOpen = () => send('color_picker_open');
export const trackColorPickerClose = () => send('color_picker_close');

export const trackShapingStart = (shape, category, borderSize) => {
  send('shaping_start', { shape, category, border_size: borderSize });
};

export const trackShapingComplete = (shape, category, borderSize) => {
  send('shaping_complete', { shape, category, border_size: borderSize });
};

// Travel Map specific events
export const trackTabChange = (tabName) => send('tab_change', { tab: tabName });

export const trackMapTypeChange = (mapType) => send('map_type_change', { map_type: mapType });

export const trackLocationToggle = (mapType, locationType, locationName, isVisited) => {
  send('location_toggle', {
    map_type: mapType,
    location_type: locationType,
    location_name: locationName,
    visited: isVisited ? 1 : 0,
  });
};

export const trackVisitedColorChange = (mapType, color) => {
  send('visited_color_change', { map_type: mapType, color });
};

export const trackMapReset = (mapType, visitedCountAtReset) => {
  send('map_reset', { map_type: mapType, visited_count_at_reset: visitedCountAtReset });
};

export const trackLabelsToggle = (mapType, enabled) => {
  send('labels_toggle', { map_type: mapType, enabled: enabled ? 1 : 0 });
};

export const trackStatsConfigChange = (mapType, position) => {
  send('stats_config_change', { map_type: mapType, position });
};

// share carries kind so reports can split shaper vs travel; dimensions vary by kind.
// shaper: { kind: 'shaper', network, shape }
// travel: { kind: 'travel', network, map_type, visited_count, total_count }
export const trackShare = ({ network, kind, shape, mapType, visitedCount, totalCount }) => {
  send('share', {
    network,
    kind,
    ...(shape !== undefined && { shape }),
    ...(mapType !== undefined && { map_type: mapType }),
    ...(visitedCount !== undefined && { visited_count: visitedCount }),
    ...(totalCount !== undefined && { total_count: totalCount }),
  });
};

